import React, { useCallback, useEffect, useState } from 'react';

import { fleetAPI } from '../../lib/api';
import { Chip, ago, apiError } from './fleetUi';

const FIELD = 'w-full bg-app-well border border-app-line-chrome rounded px-3 py-2 text-sm text-app-fg placeholder-gray-500 focus:outline-none focus:border-blue-500';

/**
 * "Add a box" (fleet:manage). A code is shown exactly once, here, right after
 * it is made: the server keeps only its hash. Boxes behind NAT cannot be
 * reached to adopt them, so the box is given the code and enrols itself on its
 * next heartbeat.
 */
export default function EnrolmentPanel({ onClose }) {
  const [codes, setCodes] = useState([]);
  const [label, setLabel] = useState('');
  const [days, setDays] = useState(7);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => fleetAPI.codes().then((r) => setCodes(r.data.codes)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  async function create(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await fleetAPI.createCode({ label: label.trim(), expires_in_days: Number(days) });
      setCreated(res.data.code);
      setCopied(false);
      setLabel('');
      load();
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id) {
    setError('');
    try {
      await fleetAPI.revokeCode(id);
      if (created?.id === id) setCreated(null);
      load();
    } catch (err) {
      setError(apiError(err));
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(created.code);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  const active = codes.filter((c) => c.state === 'active');
  const origin = window.location.origin;

  return (
    <section className="mb-5 rounded-lg border border-app-line-chrome bg-app-well">
      <div className="flex items-center justify-between px-4 py-3 border-b border-app-line-chrome">
        <h2 className="text-sm font-semibold text-app-fg">Add a box</h2>
        <button onClick={onClose} className="text-xs text-gray-500 hover:text-app-fg">Close</button>
      </div>

      <div className="p-4 space-y-4">
        {error && <div className="px-3 py-2 rounded text-sm" style={{ color: 'var(--live)', background: 'var(--live-bg)' }}>{error}</div>}

        {created ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-300">
              Enrolment code for <span className="text-app-fg font-medium">{created.label}</span>. It is shown only now; copy it before closing this panel.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <code className="flex-1 min-w-0 break-all px-3 py-2 rounded bg-app-canvas border border-app-line-chrome font-mono text-[13px] text-app-fg select-all">{created.code}</code>
              <button onClick={copy} className="px-4 py-2 text-sm rounded font-medium text-white flex-shrink-0" style={{ background: 'var(--blue)' }}>
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <ol className="text-sm text-gray-400 list-decimal list-inside space-y-1">
              <li>On the box: open <span className="font-mono text-[13px] text-gray-300">http://&lt;box&gt;/</span> → <span className="text-gray-300">Fleet</span>.</li>
              <li>Set the dashboard URL to <span className="font-mono text-[13px] text-gray-300 break-all">{origin}</span> if it is not already.</li>
              <li>Paste the code → <span className="text-gray-300">Save</span>. The box appears here within a few seconds.</li>
            </ol>
            <p className="text-xs text-gray-500">
              The code works for any number of boxes until {new Date(created.expires_at).toLocaleDateString()}, or until you revoke it.
            </p>
            <button onClick={() => setCreated(null)} className="text-xs text-blue-500 hover:text-blue-400">Make another code</button>
          </div>
        ) : (
          <form onSubmit={create} className="flex flex-col sm:flex-row gap-2 sm:items-end">
            <label className="flex-1">
              <span className="block text-xs text-gray-400 mb-1">Label (who or what it is for)</span>
              <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={80} placeholder="e.g. OB1 cameras" className={FIELD} required />
            </label>
            <label className="sm:w-32">
              <span className="block text-xs text-gray-400 mb-1">Expires after</span>
              <select value={days} onChange={(e) => setDays(e.target.value)} className={FIELD}>
                {[1, 3, 7, 14, 30].map((d) => <option key={d} value={d}>{d} day{d === 1 ? '' : 's'}</option>)}
              </select>
            </label>
            <button type="submit" disabled={busy || !label.trim()} className="px-4 py-2 text-sm rounded font-medium text-white disabled:opacity-40" style={{ background: 'var(--blue)' }}>
              {busy ? 'Creating…' : 'Create code'}
            </button>
          </form>
        )}

        <div>
          <h3 className="text-xs uppercase tracking-wider text-gray-500 mb-2">Active codes</h3>
          {active.length === 0
            ? <p className="text-xs text-gray-500">None.</p>
            : (
              <ul className="divide-y divide-app-line-chrome border border-app-line-chrome rounded">
                {active.map((c) => (
                  <li key={c.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                    <span className="text-app-fg truncate flex-1 min-w-0">{c.label}</span>
                    <Chip tone="info">{c.uses} used</Chip>
                    <span className="hidden sm:inline text-xs text-gray-500 whitespace-nowrap">
                      expires {new Date(c.expires_at).toLocaleDateString()}{c.last_used_at ? ` · last used ${ago(c.last_used_at)}` : ''}
                    </span>
                    <button onClick={() => revoke(c.id)} className="text-xs hover:underline flex-shrink-0" style={{ color: 'var(--live)' }}>Revoke</button>
                  </li>
                ))}
              </ul>
            )}
        </div>
      </div>
    </section>
  );
}
